import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import Text from "~/components-shared/Text";
import { useTranslation } from "react-i18next";
import { Card, DateBadge, Dropdown, Grid, Icon, Spinner } from "~/components-shared";
import { Chart } from "react-chartjs-2";
import { BarController, BarElement, CategoryScale, Chart as ChartJs, LinearScale, Tooltip } from "chart.js";
import styled, { useTheme } from "styled-components";
import lists from "~/lib/lists";
import { AuthoritiesEnum, CRUDActionEnum, EventSurveyStatusEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import { useDispatch, useSelector } from "react-redux";
import eventSurveysActionCreators from "~/store/eventSurveys/actions";
import toastsActionCreators from "~/store/toasts/actions";
import { useHistory } from "react-router-dom";
import eventSurveySelectors from "~/store/eventSurveys/selectors";
import { resolveDates } from "~/lib/helpers";
import moment from "moment";
import eventSurveyCharts from "~/charts/eventSurveys";
import useAbacExt from "~/hooks/abac";
const StyledGrid = styled(Grid)`
  border: 1px solid ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.borders.primary;
}};
  border-radius: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.shape.borderRadius.primary;
}};

  & > *:not(:last-child) {
    border-right: 1px solid ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.borders.primary;
}};
  }
`;
const Event = styled.div`
  display: flex;
  margin-bottom: 3em;
`;
const StyledDropdown = styled(Dropdown)`
  position: absolute;
  right: 0.5em;
  top: 0.5em;
`;
const ChartWrapper = styled.div`
  height: 250px;
  width: 80%;
  margin: 0 auto;
  flex-grow: 1;
  z-index: 1;
`;
ChartJs.register(BarController, CategoryScale, LinearScale, BarElement, Tooltip);
const EventFeedbackItemCard = _ref4 => {
  let {
    survey,
    ...rest
  } = _ref4;
  const theme = useTheme();
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const sendStatus = useSelector(eventSurveySelectors.getSendStatus(survey.id));
  const {
    userHasOneOf
  } = useAbacExt();
  const history = useHistory();
  const options = [];
  const {
    event
  } = survey;
  const summary = [{
    title: t("EventFeedbackItemCard.invited"),
    value: survey.event.attendees.length || 0
  }, {
    title: t("EventFeedbackItemCard.attended"),
    value: survey.attended.length
  }, {
    title: t("EventFeedbackItemCard.rate"),
    value: `${survey.event.attendees.length ? Math.round(survey.attended.length / survey.event.attendees.length * 100).toFixed() : 0}%`
  }];
  if (userHasOneOf([AuthoritiesEnum.EVENT_SURVEYS_WRITE], survey.createdBy.id) && survey.status !== EventSurveyStatusEnum.SENT) {
    options.push({
      id: 1,
      name: t("common.actions.edit"),
      icon: "pencil",
      action: e => {
        e.stopPropagation();
        dispatch(eventSurveysActionCreators.actionChanged(CRUDActionEnum.UPDATE, survey.id));
      }
    });
  }
  if (userHasOneOf([AuthoritiesEnum.EVENT_SURVEYS_WRITE], survey.createdBy.id) && survey.status !== EventSurveyStatusEnum.SENT) {
    options.push({
      id: 2,
      name: t("EventFeedbackItemCard.sendVia", {
        target: lists.eventSurveys.target.names[survey.target]
      }),
      icon: "share",
      action: e => {
        e.stopPropagation();
        dispatch(eventSurveysActionCreators.sendStarted(survey.id));
      }
    });
  }
  options.push({
    id: 3,
    name: t("common.actions.duplicate"),
    icon: "copy",
    loading: sendStatus === StatusStateEnum.LOADING,
    action: e => {
      e.stopPropagation();
      dispatch(eventSurveysActionCreators.createStarted({
        type: survey.type,
        target: survey.target,
        eventId: survey.event.id,
        questions: survey.questions.map(_ref5 => {
          let {
            text,
            type
          } = _ref5;
          return {
            type,
            text
          };
        })
      }, _ref6 => {
        let {
          data: newEventSurvey
        } = _ref6;
        if (newEventSurvey) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("EventSurveyCreate.eventSurveyCreated", {
              name: newEventSurvey.event.name
            }),
            timeout: 10000,
            actions: [{
              icon: "external-link",
              onClick: () => history.push(RoutesEnum.EVENT_SURVEY.replace(":eventSurveyId", newEventSurvey.id))
            }]
          }));
        }
      }));
    }
  });
  if (userHasOneOf([AuthoritiesEnum.EVENT_SURVEYS_WRITE], survey.createdBy.id) && survey.status !== EventSurveyStatusEnum.SENT) {
    options.push({
      id: 4,
      name: t("common.actions.delete"),
      icon: "bin",
      action: e => {
        e.stopPropagation();
        dispatch(eventSurveysActionCreators.actionChanged(CRUDActionEnum.DELETE, survey.id));
      }
    });
  }
  const handleView = () => {
    history.push(RoutesEnum.EVENT_SURVEY.replace(":eventSurveyId", survey.id));
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      px: 5,
      py: 5
    },
    onClick: handleView
  }, rest), !!options.length && /*#__PURE__*/React.createElement(StyledDropdown, {
    display: "inline-block",
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      display: "block",
      color: colors.text.primary
    })
  }, _ref7 => {
    let {
      isOpen,
      onItemClick
    } = _ref7;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, options.map(option => /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        onItemClick();
        if (option.action) option.action(e);
      },
      key: option.id
    }, option.loading ? /*#__PURE__*/React.createElement(Spinner, null) : /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: option.icon,
      s: {
        mr: 2
      },
      color: "primary"
    }), option.name)));
  }), /*#__PURE__*/React.createElement(Event, null, /*#__PURE__*/React.createElement(DateBadge, {
    date: event.dateFrom,
    s: {
      mt: 3,
      mr: 4
    },
    color: lists.events.types.colors(theme)[event.type],
    responseStatus: event.attendees.find(_ref8 => {
      let {
        self
      } = _ref8;
      return self;
    })?.responseStatus
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: lists.events.types.colors(theme)[event.type],
    fontSize: 3,
    weight: "semiBold",
    display: "block"
  }, lists.events.types.names[event.type]), /*#__PURE__*/React.createElement(Text, {
    element: "h5"
  }, event.name), /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "textSecondary",
    s: {
      mr: 3
    }
  }, resolveDates(event.dateFrom, event.dateTo)))), /*#__PURE__*/React.createElement(ChartWrapper, null, survey.questions.length ? /*#__PURE__*/React.createElement(Chart, eventSurveyCharts.answers(theme, survey.questions)) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    align: "center",
    display: "block",
    weight: "semiBold",
    s: {
      mt: 20
    }
  }, t("EventFeedbackItemCard.nothingToDisplay"))), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 3,
    s: {
      mt: 6
    },
    equal: true
  }, summary.map(item => /*#__PURE__*/React.createElement(Grid.Cell, {
    key: item.title,
    s: {
      px: 4,
      py: 2
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, item.title), /*#__PURE__*/React.createElement(Text, {
    element: "span",
    fontSize: 6,
    color: colors.misc.darkPurple,
    weight: "bold"
  }, item.value)))), survey.status === EventSurveyStatusEnum.SENT ? /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    align: "center",
    display: "block",
    s: {
      mt: 3
    },
    fontSize: 3
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "checkboxFill",
    s: {
      mr: 1
    },
    scale: 3
  }), "Sent via ", /*#__PURE__*/React.createElement("strong", null, lists.eventSurveys.target.names[survey.target]), " at", " ", moment(survey.sentAt).format("ddd, MMM D hh:mma")) : survey.scheduledAt ? /*#__PURE__*/React.createElement(Text, {
    color: "primary",
    align: "center",
    display: "block",
    s: {
      mt: 3
    },
    fontSize: 3
  }, "Scheduled via ", /*#__PURE__*/React.createElement("strong", null, lists.eventSurveys.target.names[survey.target]), " at", " ", moment(survey.scheduledAt).format("ddd, MMM D hh:mma")) : /*#__PURE__*/React.createElement(Text, {
    color: colors.danger.main,
    align: "center",
    display: "block",
    s: {
      mt: 3
    },
    fontSize: 3
  }, t("EventFeedbackItemCard.notScheduled")));
};
export default EventFeedbackItemCard;